import React, { useState, useMemo, useEffect } from 'react';
import { exportTableAsExcel, exportTableAsPdf } from '../../utils/exportTable';
import './DataTableWithFilters.css';

const DataTableWithFilters = ({
  data = [],
  columns = [],
  loading = false,
  loadingMessage = 'Cargando...',
  emptyMessage = 'No hay datos disponibles',
  className = '',
  searchPlaceholder = 'Buscar...',
  itemsPerPageOptions = [10, 25, 50, 100],
  defaultItemsPerPage = 10,
  // Props para paginación externa
  externalPagination = false,
  externalTotalItems = 0,
  externalTotalPages = 0,
  externalCurrentPage = 1,
  onPageChange = () => {},
  onItemsPerPageChange = () => {},
  exportConfig = null,
  getRowClassName
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isExporting, setIsExporting] = useState(false);
  
  // Usar paginación externa o interna según la configuración
  const currentPageToUse = externalPagination ? externalCurrentPage : internalCurrentPage;

  useEffect(() => {
    setItemsPerPage(defaultItemsPerPage);
  }, [defaultItemsPerPage]);

  // Filtrar datos basado en el término de búsqueda
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    
    return data.filter(item => {
      return columns.some(column => {
        if (!column.searchable && column.searchable !== undefined) return false;
        
        const rawValue = item[column.key];
        let value = rawValue;

        if (typeof column.searchFormatter === 'function') {
          value = column.searchFormatter(rawValue, item);
        } else if (typeof column.exportFormatter === 'function') {
          value = column.exportFormatter(rawValue, item);
        } else if (typeof column.formatter === 'function') {
          const formattedValue = column.formatter(rawValue, item);
          value = ['string', 'number', 'boolean'].includes(typeof formattedValue) ? formattedValue : rawValue;
        }
        
        return String(value || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns]);

  // Ordenar datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginar datos (solo para paginación interna)
  const paginatedData = useMemo(() => {
    if (externalPagination) {
      return sortedData; // Con paginación externa, los datos ya vienen paginados y se filtran localmente.
    }
    const startIndex = (currentPageToUse - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPageToUse, itemsPerPage, externalPagination]);

  // Calcular información de paginación
  const totalPagesToUse = externalPagination ? externalTotalPages : Math.ceil(sortedData.length / itemsPerPage);
  const totalItemsToUse = externalPagination ? externalTotalItems : sortedData.length;
  const startItem = totalItemsToUse === 0 ? 0 : (currentPageToUse - 1) * itemsPerPage + 1;
  const endItem = externalPagination
    ? Math.min(startItem + paginatedData.length - 1, totalItemsToUse)
    : Math.min(currentPageToUse * itemsPerPage, totalItemsToUse);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!externalPagination) {
      setInternalCurrentPage(1); // Reset to first page when searching
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    if (externalPagination) {
      onItemsPerPageChange(newItemsPerPage);
    } else {
      setInternalCurrentPage(1); // Reset to first page when changing items per page
    }
  };

  const handlePageChange = (page) => {
    if (externalPagination) {
      onPageChange(page);
    } else {
      setInternalCurrentPage(page);
    }
  };

  const resolveExportRows = async () => {
    if (typeof exportConfig?.getRows === 'function') {
      return await exportConfig.getRows();
    }

    return sortedData;
  };

  const handleExportExcel = async () => {
    if (!exportConfig || sortedData.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      exportTableAsExcel({
        rows,
        columns,
        fileName: exportConfig.fileName,
        title: exportConfig.title
      });
    } catch (error) {
      console.error('Error exportando tabla a Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!exportConfig || sortedData.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      await exportTableAsPdf({
        rows,
        columns,
        fileName: exportConfig.fileName,
        title: exportConfig.title
      });
    } catch (error) {
      console.error('Error exportando tabla a PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort sort-icon"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPageToUse - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPagesToUse, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key="first"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Visible pages
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPageToUse ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPagesToUse) {
      if (endPage < totalPagesToUse - 1) {
        buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key="last"
          className="pagination-btn"
          onClick={() => handlePageChange(totalPagesToUse)}
        >
          {totalPagesToUse}
        </button>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <div className={`data-table-container ${className}`}>
        <div className="table-loading">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`data-table-container ${className}`}>
      {/* Search and Controls */}
      <div className="table-controls">
        <div className="search-container">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Limpiar búsqueda"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        
        <div className="table-controls-right">
          {exportConfig && sortedData.length > 0 && (
            <div className="data-table-export-actions">
              <button type="button" className="export-table-btn" onClick={handleExportExcel} disabled={isExporting}>
                <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}></i>
                Excel
              </button>
              <button type="button" className="export-table-btn" onClick={handleExportPdf} disabled={isExporting}>
                <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                PDF
              </button>
            </div>
          )}

          <div className="items-per-page-container">
            <label htmlFor="itemsPerPage">Mostrar:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="items-per-page-select"
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span>registros</span>
          </div>
        </div>
      </div>

      {/* Results Info */}
      {sortedData.length > 0 && (
        <div className="table-info">
          <span className="results-info">
            Mostrando {startItem} a {endItem} de {totalItemsToUse} registros
            {!externalPagination && filteredData.length !== data.length && (
              <span className="filtered-info">
                {' '}(filtrado de {data.length} registros totales)
              </span>
            )}
          </span>
        </div>
      )}

      {/* Table */}
      {sortedData.length === 0 ? (
        <div className="table-empty">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <p>{searchTerm ? `No se encontraron resultados para "${searchTerm}"` : emptyMessage}</p>
          {searchTerm && (
            <button
              className="clear-search-btn-empty"
              onClick={() => setSearchTerm('')}
            >
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`${column.className || ''} ${column.align ? `text-${column.align}` : ''} ${column.width || ''}`}
                      style={{ width: column.width }}
                    >
                      {column.sortable ? (
                        <button
                          className="sort-button"
                          onClick={() => handleSort(column.key)}
                          title={`Ordenar por ${column.title}`}
                        >
                          <span>{column.title}</span>
                          {getSortIcon(column.key)}
                        </button>
                      ) : (
                        column.title
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className={typeof getRowClassName === 'function' ? getRowClassName(item, index) || '' : ''}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`${column.className || ''} ${column.align ? `text-${column.align}` : ''}`}
                      >
                        {column.render
                          ? column.render(item[column.key], item, index)
                          : column.formatter
                          ? column.formatter(item[column.key], item)
                          : item[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPagesToUse > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>
                  Página {currentPageToUse} de {totalPagesToUse}
                </span>
              </div>
              
              <div className="pagination-controls">
                <button
                  className="pagination-btn nav-btn"
                  onClick={() => handlePageChange(currentPageToUse - 1)}
                  disabled={currentPageToUse === 1}
                  title="Página anterior"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                {renderPaginationButtons()}
                
                <button
                  className="pagination-btn nav-btn"
                  onClick={() => handlePageChange(currentPageToUse + 1)}
                  disabled={currentPageToUse === totalPagesToUse}
                  title="Página siguiente"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTableWithFilters;
